import { Box } from "@mui/material";
import React, { useState } from "react";
import Dialog from "./Dialog";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { Button } from "../controls";
import { useTranslation } from "react-i18next";

const iconTypes = ["success", "warning", "error", "info", "question"];

// Soft tinted circle behind the status icon, keyed by icon type.
const badgeTint = {
  success: "bg-green-50",
  warning: "bg-amber-50",
  error: "bg-red-50",
  info: "bg-sky-50",
  question: "bg-slate-100",
};

const AlertIcon = ({ alert }) => {
  const sx = { fontSize: "38px" };
  switch (alert.type) {
    case "success":
      return <CheckCircleRoundedIcon color="success" sx={sx} />;
    case "warning":
      return <WarningAmberRoundedIcon color="warning" sx={sx} />;
    case "error":
      return <ErrorOutlineRoundedIcon color="error" sx={sx} />;
    case "info":
      return <InfoOutlinedIcon color="info" sx={sx} />;
    case "question":
      return <HelpOutlineRoundedIcon color="primary" sx={sx} />;
    default:
      return null;
  }
};

// A confirmation's icon reflects the CONFIRM button's tone: destructive
// (red) actions keep a cautionary icon, neutral/positive ones get a calm
// brand "question" icon instead of an alarming red error symbol.
const confirmIconType = (confirmBtnColor) => {
  switch (confirmBtnColor) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "error":
    case undefined:
    case null:
    case "":
      return "error";
    default:
      return "question";
  }
};

const AlertTextIcon = ({ alert }) => {
  const showIcon = alert.hideIcon !== true && iconTypes.includes(alert.type);
  const subtitle = alert.htmlSubtitle ? alert.htmlSubtitle : alert.subTitle;

  return (
    <div className="text-center px-[8px]">
      {showIcon ? (
        <div className="flex justify-center mt-[2px] mb-[12px]">
          <span
            className={`inline-flex items-center justify-center h-[66px] w-[66px] rounded-full ${
              badgeTint[alert.type] || "bg-gray-100"
            }`}
          >
            <AlertIcon alert={alert} />
          </span>
        </div>
      ) : (
        <div className="mt-[4px]" />
      )}
      <div className="grid gap-[8px]">
        <span className="text-[20px] leading-snug font-semibold text-gray-800">
          {alert.title}
        </span>
        {subtitle && (
          <span className="text-[14.5px] leading-relaxed text-gray-500 max-w-[340px] mx-auto">
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
};

const ConfirmationCom = (props) => {
  const { alert, setAlert } = props;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="grid gap-[20px]">
      <AlertTextIcon
        alert={{ ...alert, type: confirmIconType(alert.confirmBtnColor) }}
      />
      <div className="flex justify-center gap-[12px] w-full">
        <Button
          className="!flex-1 whitespace-nowrap"
          variant="outlined"
          size="large"
          color={alert.cancelBtnColor ? alert.cancelBtnColor : "primary"}
          onClick={() =>
            alert.onCancel
              ? alert.onCancel()
              : setAlert({ ...alert, isOpen: false })
          }
        >
          {alert.cancelBtnTitle ? alert.cancelBtnTitle : t("cancel")}
        </Button>
        <Button
          className="!flex-1 whitespace-nowrap"
          variant="contained"
          size="large"
          disableElevation
          color={alert.confirmBtnColor ? alert.confirmBtnColor : "error"}
          loading={loading}
          onClick={() => {
            setLoading(true);
            alert.onConfirm && alert.onConfirm();
          }}
        >
          {alert.confirmBtnTitle ? alert.confirmBtnTitle : t("yes")}
        </Button>
      </div>
    </div>
  );
};

export default function Alert(props) {
  const { alert, setAlert } = props;
  const { t } = useTranslation();

  return (
    <>
      {alert && (
        <Dialog
          show={alert.isOpen}
          maxWidth="md"
          PaperProps={{
            className: "min-w-[300px] !max-w-[440px] !rounded-2xl !shadow-xl",
          }}
        >
          <Box className="p-[9px]">
            {alert.type === "confirmation" ? (
              <ConfirmationCom {...{ alert, setAlert }} />
            ) : (
              <div>
                <AlertTextIcon {...{ alert }} />
                <div className="mt-[14px]">
                  <Button
                    variant="contained"
                    color={alert.type !== "" ? alert.type : "primary"}
                    fullWidth
                    size="large"
                    disableElevation
                    onClick={() => {
                      setAlert({ ...alert, isOpen: false });
                      alert.onClick && alert.onClick();
                    }}
                  >
                    {t("ok")}
                  </Button>
                </div>
              </div>
            )}
          </Box>
        </Dialog>
      )}
    </>
  );
}
